/* ===== Helper Functions ===== */
import { logger } from './logger.js?v=83.21';
import { loadScriptOnce } from './lazy-loader.js?v=83.21';

// DOM selector shorthand
export const $ = (id) => document.getElementById(id);

/** Eroare Firestore de tip permisiuni (nu merită retry automat). */
export function isFirestorePermissionDenied(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '').toLowerCase();
  return code === 'permission-denied' || msg.includes('missing or insufficient permissions');
}

let firestoreAccessDeniedLogged = false;

/** Log o singură dată per sesiune — evită zeci de erori identice în consolă. */
export function logFirestoreAccessDeniedOnce(scope, err) {
  if (!isFirestorePermissionDenied(err)) return false;
  if (!firestoreAccessDeniedLogged) {
    firestoreAccessDeniedLogged = true;
    logger.warn(
      `[${scope}] Acces Firestore blocat (reguli sau permisiuni). Pe staging: firebase deploy --only firestore --project spressjoy-staging`,
      err?.code || err?.message || err
    );
  }
  return true;
}

// Escape HTML to prevent XSS
export function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Date format helper (DD.MM.YYYY)
export function fmtDate(iso) {
  if (!iso) return 'Nedefinită';
  // doar AAAA-LL-ZZ; rezultatul e folosit și în HTML, deci nu lăsăm să treacă altceva
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : 'Nedefinită';
}

/** YYYY-MM-DD în timezone Europe/Bucharest. */
export function getBucharestDateKey(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Bucharest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

export function isWithinIsoDateRange(startDate, endDate, dateKey) {
  const start = String(startDate || '').trim();
  const end = String(endDate || '').trim();
  const today = String(dateKey || getBucharestDateKey()).trim();
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

/** Parse Firestore Timestamp / callable JSON / ISO → Date sau null. */
export function parseFirestoreTimestamp(value) {
  if (value == null || value === '') return null;
  try {
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value.toMillis === 'function') {
      const d = new Date(value.toMillis());
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const sec = value.seconds ?? value._seconds;
    if (typeof sec === 'number') {
      const nano = value.nanoseconds ?? value._nanoseconds ?? 0;
      const d = new Date(sec * 1000 + nano / 1e6);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'number') {
      const d = new Date(value < 1e12 ? value * 1000 : value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

/** Afișare dată RO; returnează „—” dacă lipsește sau e invalidă (nu „Invalid Date”). */
export function formatFirestoreDateRo(value, options = {}) {
  const { withTime = true } = options;
  const d = parseFirestoreTimestamp(value);
  if (!d) return '—';
  if (withTime) {
    return d.toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  return d.toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// SHA-256 helper with fallback
export async function sha256(str) {
  const s = String(str || '');
  // 1. Try native crypto (fastest) if available and secure context
  if (window.crypto && window.crypto.subtle && window.isSecureContext) {
    try {
      const data = new TextEncoder().encode(s);
      const hash = await crypto.subtle.digest('SHA-256', data);
      return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      logger.warn("Native crypto failed, using fallback", e);
    }
  }
  // 2. Fallback to js-sha256 library
  if (window.sha256) {
    return window.sha256(s);
  }
  // 3. Lazy-load js-sha256 if available
  try {
    await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/js-sha256/0.9.0/sha256.min.js', 'sha256', 8000);
    if (window.sha256) {
      return window.sha256(s);
    }
  } catch (e) {
    logger.warn('Failed to lazy-load sha256:', e);
  }
  // 4. Last resort (should not happen if CDN is up)
  logger.error("No SHA256 provider found!");
  return "";
}

// Normalize phone number
export function normalizePhone(raw) {
  const s = String(raw || '').trim();
  const d = s.replace(/[^\d]/g, '');
  // „+40 722 123 456” / „0040…” → „0722123456”
  if (/^0040\d{9}$/.test(d)) return `0${d.slice(4)}`;
  if (/^40\d{9}$/.test(d)) return `0${d.slice(2)}`;
  if (/^7\d{8}$/.test(d)) return `0${d}`; // mobil scris fără 0 în față
  // internațional (ex. „+49 171 …”): păstrăm „+”, ca numărul să rămână apelabil
  if (s.startsWith('+') && d && !d.startsWith('0')) return `+${d}`;
  return d;
}

// Set hidden class
export function setHidden(el, yes) {
  if (!el) return;
  yes ? el.classList.add('hidden') : el.classList.remove('hidden');
}

// Get merchant background image (first image from bgURLs)
// Use dynamic import to avoid circular dependency with settings.js
export async function getMerchantBackgroundImage() {
  const { getCurrentSettings } = await import('../services/settings.js?v=83.21');
  const currentSettings = getCurrentSettings();
  return currentSettings?.bgURLs?.[0] || currentSettings?.bgURL || null;
}

// Apply merchant background to admin page only (from Firebase)
// DOAR dacă NU există background local (local are prioritate)
export function applyMerchantBackground(pageId) {
  // Exclude clientPage and accountPage explicitly
  if (pageId === 'clientPage' || pageId === 'accountPage') return;
  
  // Only apply to adminPage
  if (pageId !== 'adminPage') return;
  
  const pageElement = document.getElementById(pageId);
  if (!pageElement) return;
  
  // Verifică dacă există background local - dacă da, nu aplicăm Firebase
  const savedBg = localStorage.getItem('accountPageBackground');
  if (savedBg) {
    // Există background local, nu aplicăm Firebase
    pageElement.classList.remove('has-merchant-bg');
    pageElement.style.removeProperty('--merchant-bg-image');
    return;
  }
  
  // Use dynamic import to avoid circular dependency
  import('../services/settings.js?v=83.21').then(settingsModule => {
    const currentSettings = settingsModule.getCurrentSettings();
    const bgImage = currentSettings?.bgURLs?.[0] || currentSettings?.bgURL || null;
    
    if (bgImage) {
      pageElement.classList.add('has-merchant-bg');
      pageElement.style.setProperty('--merchant-bg-image', `url(${bgImage})`);
    } else {
      pageElement.classList.remove('has-merchant-bg');
      pageElement.style.removeProperty('--merchant-bg-image');
    }
  }).catch(() => {
    // If import fails, just remove the class
    pageElement.classList.remove('has-merchant-bg');
    pageElement.style.removeProperty('--merchant-bg-image');
  });
}

// Apply local background to accountPage and adminPage (from localStorage)
// SINGURA sursă de background pentru aceste pagini
export function applyLocalBackground() {
  const savedBg = localStorage.getItem('accountPageBackground');
  
  // Apply to accountPage
  const accountPage = document.getElementById('accountPage');
  if (accountPage) {
    if (savedBg) {
      accountPage.classList.add('has-local-bg');
      accountPage.style.setProperty('--account-bg-image', `url(${savedBg})`);
    } else {
      accountPage.classList.remove('has-local-bg');
      accountPage.style.removeProperty('--account-bg-image');
    }
  }
  
  // Apply to adminPage (same image for both)
  const adminPage = document.getElementById('adminPage');
  if (adminPage) {
    if (savedBg) {
      // Există background local - aplicăm local
      adminPage.classList.add('has-local-bg');
      adminPage.style.setProperty('--account-bg-image', `url(${savedBg})`);
      // Asigură-te că eliminăm orice background Firebase
      adminPage.classList.remove('has-merchant-bg');
      adminPage.style.removeProperty('--merchant-bg-image');
    } else {
      // Nu există background local - eliminăm local (rămâne mov implicit)
      adminPage.classList.remove('has-local-bg');
      adminPage.classList.remove('has-merchant-bg'); // Elimină și Firebase dacă există
      adminPage.style.removeProperty('--account-bg-image');
      adminPage.style.removeProperty('--merchant-bg-image');
    }
  }
}
